<script lang="ts" setup>
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import MonthPicker from '@/corePagesModule/components/MonthPicker.vue';
import { i18n } from '@/i18n';
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';

// props
const props = defineProps<{
  store: ReturnType<typeof import('../pages/useForecastAccuracyStore').useForecastAccuracyStore>;
  defaultStartYearMonth: IYearMonth;
  defaultEndYearMonth: IYearMonth;
}>();

// 変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

const { state } = storeToRefs(props.store);

onMounted(() => {
  state.value.downloadModalState.startYearMonth = props.defaultStartYearMonth;
  state.value.downloadModalState.endYearMonth = props.defaultEndYearMonth;
});

/**
 * モーダルの表示状態
 */
const modalState = computed({
  /**
   * getter
   * @returns モーダルの表示状態
   */
  get: () => {
    return state.value.downloadModalState.isOpen;
  },
  /**
   * setter
   * @param value - 設定する値
   */
  set: (value) => {
    state.value.downloadModalState.isOpen = value;
    if (value) {
      // Reset to default values when modal opens
      state.value.downloadModalState.startYearMonth = props.defaultStartYearMonth;
      state.value.downloadModalState.endYearMonth = props.defaultEndYearMonth;
    }
  },
});

const isInvalidYearMonth = computed(() => {
  if (
    !DateService.validIYearMonth(state.value.downloadModalState.startYearMonth) ||
    !DateService.validIYearMonth(state.value.downloadModalState.endYearMonth)
  )
    return '年月を入力してください';

  const diff = DateService.calculateIYearMonthsDiff(
    state.value.downloadModalState.endYearMonth,
    state.value.downloadModalState.startYearMonth
  );
  if (diff < 0) return '開始年月より終了年月が前になっています';
  if (diff > 23) return '24ヶ月を超える期間は指定できません';
  return 'valid';
});

/**
 * 開始年月選択時の処理
 * @param yearMonth - 選択された年月
 */
const onSelectedStartYearMonth = (yearMonth: IYearMonth) => {
  state.value.downloadModalState.startYearMonth = yearMonth;
};

/**
 * 終了年月選択時の処理
 * @param yearMonth - 選択された年月
 */
const onSelectedEndYearMonth = (yearMonth: IYearMonth) => {
  state.value.downloadModalState.endYearMonth = yearMonth;
};
</script>

<template>
  <InfoModal
    :modal-state="modalState"
    :back-button-label="t('キャンセル')"
    :next-button-label="t('ダウンロード')"
    :is-disabled-next-button="isInvalidYearMonth !== 'valid'"
    @on-back="modalState = false"
    @on-next="store.onDownloadApply(state.downloadModalState.startYearMonth, state.downloadModalState.endYearMonth)"
  >
    <template #title>{{ t('内示正答率のダウンロード') }}</template>
    <template #description>
      <div>{{ t('下記から出力期間を選んでください') }}</div>
      <div class="download-accuracy-rate-modal__forecast-name">{{ state.downloadModalState.targetForecastName }}</div>
    </template>

    <div v-if="isInvalidYearMonth !== 'valid'" class="error-msg">
      {{ t(isInvalidYearMonth) }}
    </div>
    <div class="download-accuracy-rate-modal-content__main-content main-content">
      <MonthPicker
        class="main-content__calendar download-accuracy-rate-calendar"
        :model-value="state.downloadModalState.startYearMonth"
        :locale="locale"
        :prop-format="t('MonthPickerFormat')"
        :max-date="state.downloadModalState.endYearMonth"
        @update:model-value="onSelectedStartYearMonth($event)"
      />
      {{ t('から') }}
      <MonthPicker
        class="main-content__calendar download-accuracy-rate-calendar"
        :model-value="state.downloadModalState.endYearMonth"
        :locale="locale"
        :prop-format="t('MonthPickerFormat')"
        :min-date="state.downloadModalState.startYearMonth"
        @update:model-value="onSelectedEndYearMonth($event)"
      />
      {{ t('まで') }}
    </div>
  </InfoModal>
</template>

<style lang="scss" scoped>
.download-accuracy-rate-modal-content {
  height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 24px;
}

.download-accuracy-rate-modal__forecast-name {
  margin-top: 8px;
  font-weight: 700;
  color: $textDefault;
}

.main-content {
  background-color: $backgroundDefault;
  border-radius: 8px;
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: 8px;
  align-items: center;
  font-size: $fontSizeS;
  font-weight: 400;
  padding: 16px;

  &__calendar {
    min-width: 141px;
    max-width: 141px;
    height: 36px;
    background-color: $semanticWhite;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    display: grid;
    place-items: center;
  }
}

.error-msg {
  color: $semanticError;
  font-weight: 700;
}
</style>

<style lang="scss">
.download-accuracy-rate-calendar input {
  font-family: Noto Sans JP;
  font-size: $fontSizeM;
  font-weight: 700;
  color: $textDefault;
}
</style>
